// Series validity wording — one source of truth for every screen that has
// to tell a guest (or the reception desk) when a series runs out.
// Mirrors backend/routes/series_validity.py exactly: four modes, and the
// `days_from_first_use` clock only starts once a session is claimed.

const DAY_MS = 86400000;

export const validityMode = (row) =>
  row?.validity_mode || (row?.validity_days ? "days_from_purchase" : "never");

export const fmtValidityDate = (value) => {
  if (!value) return "";
  const d = new Date(String(value).length <= 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

/** Wording for a CATALOG template — the contract, before anything is sold.
 *  e.g. "Valid 30 days from first use" */
export function templateValidityText(tmpl) {
  const mode = validityMode(tmpl);
  const days = Number(tmpl?.validity_days || 0);
  if (mode === "on_date") {
    return tmpl?.validity_date ? `Valid until ${fmtValidityDate(tmpl.validity_date)}` : "Valid until a fixed date";
  }
  if (mode === "days_from_first_use") {
    return `Valid ${days} day${days === 1 ? "" : "s"} from first use`;
  }
  if (mode === "days_from_purchase") {
    return `Valid ${days} day${days === 1 ? "" : "s"} from purchase`;
  }
  return "No expiry";
}

/** Wording for an ISSUED client series — the real clock.
 *  Returns `{ text, tone }` where tone is ok | warn | danger | muted. */
export function clientSeriesValidity(cs, now = new Date()) {
  const mode = validityMode(cs);
  const days = Number(cs?.validity_days || 0);
  const exp = cs?.expires_at;

  if (!exp) {
    if (mode === "days_from_first_use") {
      return {
        text: `Valid ${days} day${days === 1 ? "" : "s"} from first use · clock starts on the first session`,
        tone: "muted",
      };
    }
    return { text: "No expiry", tone: "muted" };
  }

  const end = new Date(String(exp).replace("Z", "+00:00"));
  if (Number.isNaN(end.getTime())) return { text: "No expiry", tone: "muted" };
  const left = Math.ceil((end.getTime() - now.getTime()) / DAY_MS);
  const on = fmtValidityDate(exp);
  if (left < 0) return { text: `Expired ${on}`, tone: "danger" };
  if (left === 0) return { text: `Expires today · ${on}`, tone: "danger" };
  return {
    text: `Expires ${on} · ${left} day${left === 1 ? "" : "s"} left`,
    tone: left <= 7 ? "warn" : "ok",
  };
}

export const VALIDITY_TONE_CLASS = {
  ok: "text-emerald-700",
  warn: "text-amber-700",
  danger: "text-rose-700",
  muted: "text-muted-foreground",
};
